/**
 * LegalDocViewer — renders a counsel-drafted policy document as a styled
 * page. Reads the extracted markdown from frontend/src/legal-rendered/
 * (produced by bin/extract_legal_docs.py).
 *
 * Why a custom renderer instead of react-markdown:
 *   - the extracted text uses ONLY # / ## / ### headings + plain paragraphs.
 *     A 30-line parser keeps us free of a new dep + saves a Webpack chunk.
 *   - we want full Tailwind control over the typography to match the brand.
 *
 * Usage:
 *
 *   import { LegalDocViewer } from "@/components/LegalDocViewer";
 *   import termsMd from "@/legal-rendered/terms_of_service.md?raw";
 *
 *   <LegalDocViewer
 *     title="Terms of Service"
 *     subtitle="Marketplace user terms (B2C + B2B)"
 *     policyId="terms_of_service"
 *     content={termsMd}
 *   />
 *
 * Acceptance:
 *   pass `withAcceptance={true}` to render the accept-checkbox at the
 *   bottom. On click, posts to /api/legal/acceptances and calls onAccept.
 */
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, CheckCircle2 } from "lucide-react";
import { API } from "@/App";
import { getUserToken } from "@/lib/authStorage";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { Button } from "@/components/ui/button";


/**
 * Tiny markdown-to-JSX renderer. Handles:
 *   '# Heading' → <h1>
 *   '## Heading' → <h2>
 *   '### Heading' → <h3>
 *   blank lines → paragraph break
 *   everything else → <p>
 *
 * No bold/italic/list/link parsing — the extracted policies don't use them.
 * If we ever need richer formatting, swap to react-markdown.
 */
function renderMarkdown(md) {
  const lines = md.split("\n");
  const blocks = [];
  let buffer = [];

  const flushBuffer = (key) => {
    if (buffer.length === 0) return;
    const text = buffer.join(" ").trim();
    if (text) {
      blocks.push(
        <p key={key} className="text-slateline-700 leading-relaxed mb-4">
          {text}
        </p>
      );
    }
    buffer = [];
  };

  lines.forEach((rawLine, i) => {
    const line = rawLine.replace(/\r$/, "");

    if (line.startsWith("#### ")) {
      flushBuffer(`p-${i}`);
      blocks.push(<h4 key={`h4-${i}`} className="font-display text-lg mt-6 mb-2 text-ink">{line.slice(5)}</h4>);
    } else if (line.startsWith("### ")) {
      flushBuffer(`p-${i}`);
      blocks.push(<h3 key={`h3-${i}`} className="font-display text-xl mt-8 mb-3 text-ink">{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      flushBuffer(`p-${i}`);
      blocks.push(<h2 key={`h2-${i}`} className="font-display text-2xl mt-10 mb-4 text-ink">{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      flushBuffer(`p-${i}`);
      blocks.push(<h2 key={`h1-${i}`} className="font-display text-2xl mt-10 mb-4 text-ink">{line.slice(2)}</h2>);
    } else if (line.trim() === "") {
      flushBuffer(`p-${i}`);
    } else {
      buffer.push(line);
    }
  });
  flushBuffer("p-end");

  return blocks;
}


/**
 * Computes SHA-256 of a string in the browser using SubtleCrypto.
 * Matches the hash format used by bin/extract_legal_docs.py +
 * legal_audit.policy_versions.text_hash.
 */
async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}


export default function LegalDocViewer({
  title,
  subtitle,
  policyId,
  policyVersion = "1.1.0",
  content,
  withAcceptance = false,
  alreadyAccepted = false,
  onAccept,
  showMarketingChrome = true,
  // backFallback: where to send the user if there's no browser history
  // (direct URL / new tab). Defaults to "/". Callers from the admin
  // panel can pass "/admin/legal" to keep CLCO admins inside the admin
  // surface even when they open a policy in a new tab.
  backFallback = "/",
}) {
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(alreadyAccepted);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const blocks = useMemo(() => renderMarkdown(content || ""), [content]);

  // Smart back: walk browser history if possible, else fall back to a
  // sensible default. Handles the new-tab case (window.history.length<=1)
  // where navigate(-1) would silently no-op.
  function smartBack() {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(backFallback);
    }
  }

  async function handleAccept() {
    setAccepting(true);
    setError(null);
    try {
      const textHash = await sha256Hex(content);
      const token = getUserToken();
      const res = await fetch(`${API}/legal/acceptances`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          policy_id: policyId,
          policy_version: policyVersion,
          text_hash_at_acceptance: textHash,
        }),
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      setAccepted(true);
      if (onAccept) {
        const data = await res.json();
        onAccept(data);
      }
    } catch (e) {
      setError(e.message || "Could not record acceptance. Try again.");
    } finally {
      setAccepting(false);
    }
  }

  const body = (
    <div className="max-w-4xl mx-auto px-6 md:px-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {showMarketingChrome && (
          <button
            type="button"
            onClick={smartBack}
            className="inline-flex items-center gap-2 text-slateline-500 hover:text-ink mb-8 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}

        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-brand-green/10 rounded-full flex items-center justify-center">
            <FileText className="w-6 h-6 text-brand-green" />
          </div>
          <div>
            <h1 className="font-display text-4xl">{title}</h1>
            {subtitle && <p className="text-slateline-500">{subtitle}</p>}
            <p className="text-slateline-400 text-sm mt-1">
              Version {policyVersion} · effective from the date shown in the document
            </p>
          </div>
        </div>

        <div className="bg-canvas-card border border-slateline-200 rounded-sm p-8 mb-8">
          {blocks}
        </div>

        {withAcceptance && (
          <div className="bg-brand-green/5 border border-brand-green/20 rounded-sm p-6 mb-8">
            {accepted ? (
              <div className="flex items-center gap-2 text-brand-green">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">You accepted this document.</span>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-slateline-700">
                  By clicking <span className="italic text-brand-green">I accept</span> you confirm
                  you have read the text above and agree to be bound by it. Your
                  acceptance is recorded with the date, your IP, and a cryptographic
                  hash of this page's text.
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleAccept}
                    disabled={accepting}
                    className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md"
                  >
                    {accepting ? "Recording…" : "I accept"}
                  </Button>
                  {error && <span className="text-sm text-red-600">{error}</span>}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );

  if (!showMarketingChrome) return body;

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />
      <div className="pt-32 pb-24">{body}</div>
      <MarketingFooter />
    </div>
  );
}

export { LegalDocViewer };
